import FeatureFlag from "@/domain/enums/FeatureFlag";
import {
    SpeechOptions,
    useAiWriting,
    useFeatureFlag,
} from "@cs_teamwaydev/mailvista-sdk_react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "./utils/useToast";

export enum Tone {
    CORRECT = SpeechOptions.CORRIGE,
    EXTEND = SpeechOptions.RALLONGE,
    SHORTCUTS = SpeechOptions.RACCOURCIS,
    FRIENDLY = SpeechOptions.AMICAL,
    NEUTRAL = SpeechOptions.NEUTRE,
    FORMAL = SpeechOptions.FORMEL,
}

const useGenerateAnswerWithIA = (): {
    generateAnswer: (
        contentToReplace: string,
        newMailContent: string,
        mailContent: string,
        tone: Tone,
    ) => Promise<string | null>;
    isLoading: boolean;
} => {
    const aiWriting = useAiWriting();
    const { toast } = useToast();
    const { t } = useTranslation();
    const isAssistantPromptEnabled = useFeatureFlag(
        FeatureFlag.ASSISTANT_ENABLE_PROMPT,
    );
    const [isLoading, setIsLoading] = useState(false);

    const generateAnswer = async (
        contentToReplace: string,
        newMailContent: string,
        mailContent: string,
        tone: Tone,
    ): Promise<string | null> => {
        if (!isAssistantPromptEnabled) {
            toast({
                title: t("errors.global.title"),
                description: t("errors.assistantFeatureDisabled"),
                variant: "destructive",
            });

            throw new Error("Assistant prompt feature is disabled");
        }

        try {
            setIsLoading(true);

            const result = await aiWriting.generateAnswerWithAI(
                newMailContent,
                mailContent,
                tone as unknown as SpeechOptions,
                contentToReplace,
            );

            if (!result) {
                throw new Error(t("errors.generateAnswer"));
            }

            return result;
        } catch (error) {
            toast({
                title: t("errors.global.title"),
                description: t("errors.generateAnswer"),
                variant: "destructive",
            });

            return null;
        } finally {
            setIsLoading(false);
        }
    };

    return {
        generateAnswer,
        isLoading,
    };
};

export default useGenerateAnswerWithIA;
